import React, { useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import ChoiceWheel from '../components/shared/ChoiceWheel';
import usePendingApprovals from '../hooks/usePendingApprovals';
import { useMyOnboarding } from '../hooks/useQueries';
import { useMySurveys } from '../hooks/useSurveys';
import { countSurveysWaiting } from '../lib/surveys';

export default function HomePage() {
  const auth = useAuth();

  const name = auth.employee?.first_name || 'there';
  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';

  // The same queue the approvals page counts, so the two can never disagree.
  const { canViewExpenses, canApproveTimeOff, pendingTotal } = usePendingApprovals();
  const canOpenAdmin = [
    'manage_all_employees',
    'manage_coached_employees',
    'manage_roles',
    'manage_hiring',
    'manage_resumes',
    'manage_rd_credits',
    'manage_expense_vendors',
    'manage_mileage_rates',
  ].some((permission) => auth.hasPermission(permission));

  // Somebody part-way through onboarding gets it first on the wheel, with what
  // is left to do on the badge. It disappears the moment they finish.
  const onboarding = useMyOnboarding({ enabled: true });
  const onboardingState = onboarding.data || null;
  const isOnboarding = onboardingState?.assigned === true && onboardingState.status !== 'completed';
  const onboardingRemaining = isOnboarding
    ? Math.max((onboardingState.total_steps || 0) - (onboardingState.completed_count || 0), 0)
    : 0;

  // Surveys sent to them still waiting for an answer, on the tile's badge.
  const mySurveys = useMySurveys();
  const surveysWaiting = countSurveysWaiting(mySurveys.data);

  // Quick actions and admin destinations share one wheel; each entry is only
  // rendered when the signed-in user can actually reach that page.
  const items = useMemo(() => ([
    {
      key: 'onboarding',
      show: isOnboarding,
      path: '/onboarding',
      icon: 'bi-signpost-2',
      title: 'Finish Your Onboarding',
      description: 'Pick up where you left off in your onboarding steps',
      badge: onboardingRemaining > 0 ? onboardingRemaining : undefined,
    },
    {
      key: 'time-off',
      show: true,
      path: '/time-off',
      icon: 'bi-calendar-plus',
      title: 'Time Off',
      description: 'Request, view, or modify time off',
    },
    {
      key: 'me',
      show: true,
      path: '/me',
      icon: 'bi-person-circle',
      title: 'Update Your Info',
      description: 'Edit your profile, contact details, and preferences',
    },
    {
      key: 'directory',
      show: true,
      path: '/directory',
      icon: 'bi-people',
      title: 'Company Directory',
      description: 'Look up anyone in the company and how to reach them',
    },
    {
      key: 'view-hours',
      show: true,
      path: '/view-hours',
      icon: 'bi-clock-history',
      title: 'Hours and Timesheet',
      description: 'Check your hours target and pull your TimeCamp totals',
    },
    {
      key: 'expenses',
      show: true,
      path: '/expenses',
      icon: 'bi-receipt',
      title: 'Submit an Expense',
      description: 'Submit and track expense reimbursements',
    },
    {
      key: 'surveys',
      show: true,
      path: '/surveys',
      icon: 'bi-ui-checks',
      title: 'Surveys',
      description: 'Answer the surveys sent to you',
      badge: surveysWaiting > 0 ? surveysWaiting : undefined,
    },
    {
      key: 'approvals',
      show: canApproveTimeOff || canViewExpenses,
      path: '/approvals',
      icon: 'bi-check-circle',
      title: 'Approvals',
      description: 'Review and action pending time off and expense requests',
      // Only when there is something waiting: a nought on a dashboard tile is
      // noise, where in the queue itself it is the answer.
      badge: pendingTotal > 0 ? pendingTotal : undefined,
    },
    {
      key: 'admin',
      show: canOpenAdmin,
      path: '/admin',
      icon: 'bi-shield-lock',
      title: 'Admin',
      description: 'Open employee, permission, and company management tools',
    },
  ].filter((item) => item.show)), [
    canApproveTimeOff,
    canViewExpenses,
    canOpenAdmin,
    pendingTotal,
    isOnboarding,
    onboardingRemaining,
    surveysWaiting,
  ]);

  return <ChoiceWheel items={items} title={`${greeting}, ${name}`} />;
}
